import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUser } from '../contexts/UserContext';
import { useBrowser } from '../contexts/BrowserContext';
import { ProductModel } from '../models/ProductModel';
import { API_TOKEN, API_URL, GetClientType } from '../config';

export const useProductData = (defaultCategory = undefined) => {
    const location = useLocation();
    const { t } = useTranslation();
    const { browser } = useBrowser();
    const { user, setIsLoginModalOpen } = useUser();
    const cancelTokenSourceRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [products, setProducts] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(16);

    const determinePageType = () => {
        switch (location.pathname) {
            case "/favorites": return "favorites";
            case "/download": return "download";
            default: return "any";
        }
    };

    const fetchData = async (forcePage = -1) => {
        const page = determinePageType();
        const currentPage = forcePage > 0 ? forcePage : pageNumber;
        setHasMore(true);

        try {
            if (cancelTokenSourceRef.current) {
                cancelTokenSourceRef.current.cancel('Operation canceled due to new request.');
            }
            cancelTokenSourceRef.current = axios.CancelToken.source();

            const params = new URLSearchParams({
                client: GetClientType(),
                "page-size": pageSize,
                page: currentPage,
            });

            if (user?.username) {
                params.append("serial", user.username);
            }

            let endpoint = `${API_URL}/get-items.php?${params.toString()}`;
            if (page === "favorites") {
                endpoint = `${API_URL}/extra/api_favorites.php?action=get&serial=${user.username}&${params.toString()}`;
            }

            const response = await axios.get(endpoint, { cancelToken: cancelTokenSourceRef.current.token });

            if (response?.data?.items?.length) {
                const newProducts = response.data.items.map(product => {
                    const productModel = new ProductModel(
                        product.id,
                        product.title,
                        product.description,
                        product.category,
                        0,
                        product.images?.[0],
                        product.images,
                        product.fileName,
                        product.is_favourite ?? false,
                        !product.license || product.license === "free"
                    );
                    if (page === "download" && product.exists === false) {
                        browser.deleteFile(productModel);
                    }
                    return productModel;
                });

                setProducts(prevProducts => [...prevProducts, ...newProducts]);
                if (response.data.items.length < pageSize) setHasMore(false);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error('Error fetching data:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchMoreProducts = () => setPageNumber(prev => prev + 1);

    const toggleLike = productId => {
        if (!localStorage.getItem("edexLicenceToken") && !user?.username) {
            setIsLoginModalOpen(true);
            return;
        }
        setProducts(prevProducts => prevProducts.map(product =>
            product.id === productId ? { ...product, liked: !product.liked } : product
        ));
        sendLike(productId, user.username);
    };

    const sendLike = (productId, userSerial) => {
        products.forEach(product => {
            if (product.id === productId) {
                product.liked ? removeFromFavorites(productId, userSerial) : addToFavorites(productId, userSerial);
            }
        });
    };

    const modifyFavorites = async (productId, userSerial, action) => {
        const params = new URLSearchParams({ client: GetClientType(), serial: userSerial, product_id: productId });
        await axios.get(`${API_URL}/extra/api_favorites.php?action=${action}&${params.toString()}`, {
            headers: { Token: API_TOKEN },
            cancelToken: cancelTokenSourceRef.current.token,
        });
    };

    const addToFavorites = (productId, userSerial) => modifyFavorites(productId, userSerial, "add");
    const removeFromFavorites = (productId, userSerial) => modifyFavorites(productId, userSerial, "remove");

    useEffect(() => {
        if (pageNumber > 1) fetchData();
    }, [pageNumber]);

    return { loading, hasMore, products, fetchMoreProducts, toggleLike };
};
